import React, { useEffect, useState } from 'react';
import { motion, easeOut } from 'framer-motion';
import { Target, Eye, Users, Award, Phone, Mail, ChevronDown, MapPin } from 'lucide-react';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { Button } from '../components/ui/button';
import NavigationHeader from "../components/NavigationHeader";

const About = () => {
  const { settings } = useSiteSettings();
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setIsVisible(true);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // تأثيرات الحركة
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { 
        duration: 0.6,
        ease: easeOut // use the imported easing function
      }
    }
  };

  return (
    <>      <NavigationHeader />

    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-gray-100">
      {/* القسم العلوي */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-800 text-white py-20 relative overflow-hidden">
        {/* تأثيرات خلفية دائرية */}
        <div className="absolute top-0 left-0 w-full h-full opacity-10">
          <div className="absolute top-20 left-20 w-40 h-40 rounded-full bg-white"></div>
          <div className="absolute bottom-10 right-20 w-60 h-60 rounded-full bg-white"></div>
        </div>
        
        <div className="container mx-auto px-4 text-center relative z-10">
          {/* شعار مع حركة طفو */}
          <motion.div 
            className="mb-8"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-white rounded-full p-3 inline-block shadow-xl">
              <img 
                src={settings.logo_url} 
                alt={`شعار ${settings.site_title}`} 
                className="h-28 w-auto mx-auto rounded-full"
              />
            </div>
          </motion.div>
          
          <motion.h1 
            className="text-4xl md:text-5xl font-bold mb-6 leading-tight"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {settings.about_title}
          </motion.h1>
          
          <motion.p 
            className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {settings.about_content}
          </motion.p>
          
          <motion.div 
            className="mt-12 animate-bounce"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5, duration: 1 }}
          >
            <ChevronDown className="h-8 w-8 mx-auto" />
          </motion.div>
        </div>
      </div>

      {/* قسم المهمة والرؤية */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">
          {/* المهمة */}
          <motion.div
            initial={{ x: -50, opacity: 0 }}
            animate={isVisible ? { x: 0, opacity: 1 } : {}}
            transition={{ duration: 0.7 }}
          >
            <div className="bg-white rounded-2xl shadow-xl border border-blue-100 overflow-hidden transform transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 h-full">
              <div className="p-8">
                <div className="flex items-center mb-6">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Target className="h-8 w-8 text-blue-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mr-4">مهمتنا</h2>
                </div>
                <p className="text-gray-600 text-lg leading-relaxed">
                  {settings.about_mission}
                </p>
              </div>
              <div className="bg-blue-50 px-8 py-4 border-t border-blue-100">
                <div className="flex space-x-2">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="w-3 h-3 rounded-full bg-blue-300"></div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
          
          {/* الرؤية */}
          <motion.div
            initial={{ x: 50, opacity: 0 }}
            animate={isVisible ? { x: 0, opacity: 1 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <div className="bg-white rounded-2xl shadow-xl border border-indigo-100 overflow-hidden transform transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 h-full">
              <div className="p-8">
                <div className="flex items-center mb-6">
                  <div className="bg-indigo-100 p-3 rounded-lg">
                    <Eye className="h-8 w-8 text-indigo-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mr-4">رؤيتنا</h2>
                </div>
                <p className="text-gray-600 text-lg leading-relaxed">
                  {settings.about_vision}
                </p>
              </div>
              <div className="bg-indigo-50 px-8 py-4 border-t border-indigo-100">
                <div className="flex space-x-2">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="w-3 h-3 rounded-full bg-indigo-300"></div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* خدماتنا */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={isVisible ? { y: 0, opacity: 1 } : {}}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">خدماتنا</h2>
          
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            animate={isVisible ? "visible" : "hidden"}
          >
            {/* بطاقة الباحثين عن السكن */}
            <motion.div 
              className="group"
              variants={itemVariants}
            >
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden transform transition-all duration-300 group-hover:shadow-xl group-hover:-translate-y-2 h-full border border-blue-50">
                <div className="p-6 text-center">
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-200 transition-colors">
                    <Users className="h-8 w-8 text-blue-600 group-hover:text-blue-800 transition-colors" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">للباحثين عن السكن</h3>
                  <p className="text-gray-600">
                    نوفر منصة سهلة للبحث عن العقارات المناسبة مع خيارات تصفية متقدمة
                  </p>
                </div>
                <div className="bg-gradient-to-r from-blue-50 to-white px-6 py-4 border-t border-blue-100">
                  <ul className="space-y-2 text-gray-600 text-sm">
                    <li className="flex items-center">
                      <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                      <span>بحث متقدم بفلاتر متعددة</span>
                    </li>
                    <li className="flex items-center">
                      <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                      <span>جولات افتراضية للمعاينة</span>
                    </li>
                    <li className="flex items-center">
                      <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                      <span>حجوزات مباشرة عبر المنصة</span>
                    </li>
                  </ul>
                </div>
              </div>
            </motion.div>
            
            {/* بطاقة ملاك العقارات */}
            <motion.div 
              className="group"
              variants={itemVariants}
            >
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden transform transition-all duration-300 group-hover:shadow-xl group-hover:-translate-y-2 h-full border border-green-50">
                <div className="p-6 text-center">
                  <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-200 transition-colors">
                    <Award className="h-8 w-8 text-green-600 group-hover:text-green-800 transition-colors" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">لملاك العقارات</h3>
                  <p className="text-gray-600">
                    نساعد الملاك في عرض عقاراتهم والوصول إلى أكبر عدد من الباحثين
                  </p>
                </div>
                <div className="bg-gradient-to-r from-green-50 to-white px-6 py-4 border-t border-green-100">
                  <ul className="space-y-2 text-gray-600 text-sm">
                    <li className="flex items-center">
                      <div className="w-2 h-2 rounded-full bg-green-500 mr-2"></div>
                      <span>عروض مميزة للعقارات</span>
                    </li>
                    <li className="flex items-center">
                      <div className="w-2 h-2 rounded-full bg-green-500 mr-2"></div>
                      <span>إدارة العقارات عن بعد</span>
                    </li>
                    <li className="flex items-center">
                      <div className="w-2 h-2 rounded-full bg-green-500 mr-2"></div>
                      <span>تقارير أداء مفصلة</span>
                    </li>
                  </ul>
                </div>
              </div>
            </motion.div>
            
            {/* بطاقة خدمات متميزة */}
            <motion.div 
              className="group"
              variants={itemVariants}
            >
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden transform transition-all duration-300 group-hover:shadow-xl group-hover:-translate-y-2 h-full border border-purple-50">
                <div className="p-6 text-center">
                  <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-purple-200 transition-colors">
                    <Target className="h-8 w-8 text-purple-600 group-hover:text-purple-800 transition-colors" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">خدمات متميزة</h3>
                  <p className="text-gray-600">
                    نقدم خدمات إضافية مثل إدارة العقارات والاستشارات العقارية
                  </p>
                </div>
                <div className="bg-gradient-to-r from-purple-50 to-white px-6 py-4 border-t border-purple-100">
                  <ul className="space-y-2 text-gray-600 text-sm">
                    <li className="flex items-center">
                      <div className="w-2 h-2 rounded-full bg-purple-500 mr-2"></div>
                      <span>استشارات عقارية متخصصة</span>
                    </li>
                    <li className="flex items-center">
                      <div className="w-2 h-2 rounded-full bg-purple-500 mr-2"></div>
                      <span>إدارة العقارات بالكامل</span>
                    </li>
                    <li className="flex items-center">
                      <div className="w-2 h-2 rounded-full bg-purple-500 mr-2"></div>
                      <span>تقييم عقاري دقيق</span>
                    </li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* معلومات التواصل */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={isVisible ? { y: 0, opacity: 1 } : {}}
          transition={{ duration: 0.7, delay: 0.6 }}
          className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-xl overflow-hidden"
        >
          <div className="p-10 text-white">
            <h2 className="text-3xl font-bold mb-6 text-center">تواصل معنا</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <motion.div 
                className="bg-white bg-opacity-10 rounded-xl p-6 backdrop-blur-sm border border-white border-opacity-20"
                whileHover={{ scale: 1.03 }}
              >
                <div className="flex flex-col items-center text-center">
                  <div className="bg-white bg-opacity-20 p-3 rounded-full mb-4">
                    <Phone className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">الهاتف</h3>
                  <Button 
                    asChild 
                    className="w-full bg-white bg-opacity-10 hover:bg-opacity-20 text-white border-white border-opacity-30 mt-2"
                  >
                    <a 
                      href={`https://wa.me/${settings.contact_phone.replace(/[\s\-\+]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2"
                    >
                      <Phone className="h-4 w-4" />
                      {settings.contact_phone}
                    </a>
                  </Button>
                </div>
              </motion.div>
              
              <motion.div 
                className="bg-white bg-opacity-10 rounded-xl p-6 backdrop-blur-sm border border-white border-opacity-20"
                whileHover={{ scale: 1.03 }}
              >
                <div className="flex flex-col items-center text-center">
                  <div className="bg-white bg-opacity-20 p-3 rounded-full mb-4">
                    <Mail className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">البريد الإلكتروني</h3>
                  <Button 
                    variant="outline" 
                    asChild 
                    className="w-full bg-white bg-opacity-10 hover:bg-opacity-20 text-white border-white border-opacity-30 mt-2"
                  >
                    <a 
                      href={`mailto:${settings.contact_email}`}
                      className="flex items-center justify-center gap-2"
                    >
                      <Mail className="h-4 w-4" />
                      {settings.contact_email}
                    </a>
                  </Button>
                </div>
              </motion.div>
              
              <motion.div 
                className="bg-white bg-opacity-10 rounded-xl p-6 backdrop-blur-sm border border-white border-opacity-20"
                whileHover={{ scale: 1.03 }}
              >
                <div className="flex flex-col items-center text-center">
                  <div className="bg-white bg-opacity-20 p-3 rounded-full mb-4">
                    <MapPin className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">العنوان</h3>
                  <p className="text-blue-100 text-center p-3 bg-white bg-opacity-10 rounded-lg mt-2">
                    {settings.contact_address}
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
      
      
    </div></>
  );
};

export default About;